import { useEffect } from 'react';
import { api, ApiError } from '@/shared/api/client';
import { useMetadataStore, EntityMeta } from '@/shared/store/metadataStore';
import { useAuthStore } from '@/shared/store/authStore';

// Hydrates the Entity Registry once the user is authenticated, mirroring
// useAuth()'s /auth/me pattern. The registry drives generic UI
// (EntityDetailExtras, and later dynamic forms/lists/reports).
export function useMetadata() {
  const { registry, ready, setRegistry, setReady } = useMetadataStore();
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    if (ready) return;
    if (!user) return; // /meta is tenant-scoped — only fetch once logged in
    // A temporary password blocks every API call but the change itself: wait,
    // or the failed fetch would leave the registry empty after the change.
    if (user.must_change_password) return;
    let mounted = true;
    (async () => {
      try {
        const data = await api.get<EntityMeta[]>('/meta/registry');
        if (!mounted) return;
        setRegistry(data || []);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 401) {
          console.error('useMetadata: /meta/registry failed', err);
        }
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => { mounted = false; };
  }, [ready, user, setRegistry, setReady]);

  const byKey = (key: string): EntityMeta | null => registry.find((e) => e.key === key) || null;
  const detailRoute = (key: string, id: string): string | null => {
    const e = byKey(key);
    return e?.routes.detail ? e.routes.detail.replace(':id', id) : null;
  };

  return { registry, ready, byKey, detailRoute };
}
